import moment from "moment";
import { useEffect, useState } from "react";
import { Message } from "semantic-ui-react";
import { Appointment } from "../../modals/Appointment";
import { TaskPayload } from "../../modals/TaskPayload";
import IBSService from "../../Services/IBSService";
import { bookLoanCar } from "../../Services/LockBoxService";
import PickUpCalendar from "./PickUpCalendar/PickUpCalendar";
import PickUpDisplay from "./PickUpDisplay/PickUpDisplay";
import PickUpStepper from "./PickUpStepper/PickUpStepper";
import PickUpSummary from "./PickUpSummary/PickUpSummary";
import PickUpTime from "./PickUpTime/PickUpTime";
import { JobEstimationDetail } from "../../modals/JobEstimationDetail";

interface PickUpProps {
  estimateDetails: JobEstimationDetail;
}

const PickUp = (props: PickUpProps) => {
  const ibsService = new IBSService();

  const taskListId = "91f8ce7c-d18c-4ba9-8f54-8c645979ac73";

  const [isDone, setIsDone] = useState(false);
  const [pickTask, setPickTask] = useState<Appointment>();

  const [step, setStep] = useState(1);
  const [pickUpDate, setPickUpDate] = useState();
  const [pickUpTime, setPickUpTime] = useState(null);
  const [lockBox, setLockBox] = useState(null);
  const [isLockBoxAvailable, setIsLockBoxAvailable] = useState(true);

  const [showLoader, setShowLoader] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    checkIfDone();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onConfirm = async () => {
    setShowLoader(true);
    try {
      await assignLockBox();
      await addTask();
      await checkIfDone();
      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
      }, 2000);
    } catch (err) {
      throw new Error("Data could not be fetched!");
    } finally {
      setShowLoader(false);
    }
  };

  const addTask = async () => {
    let estimateDetails: JobEstimationDetail = props.estimateDetails;
    let url = `https://portal.flowautobody.com.au/?estimateid=${estimateDetails?.estimateNumber}&step=booking`;
    const taskObj: TaskPayload = {
      Title: `${
        estimateDetails?.ownerName != null ? estimateDetails?.ownerName : ""
      } - ${estimateDetails.estimateNumber} (${url})`,
      assignments: [
        {
          id: estimateDetails?.estimateId,
          userName: estimateDetails.estimateNumber.toString(),
          type: "Estimate",
          lastStarted: new Date(),
          isDeleted: false
        }
      ],
      AssignDate: new Date(),
      additionalNote: `Pick Up Date:- ${pickUpDate}\nPick Up Time:- ${pickUpTime}\nLock Box:-${lockBox?.["registration"]}`,
      checklistItems: []
    };

    if (lockBox) {
      taskObj.checklistItems?.push({
        caption: "Set key in assigned lock box",
        isChecked: false,
        id: "",
        isDeleted: false,
        isRenamed: false
      });
    }

    await ibsService.addTask(taskListId, taskObj);
  };

  const assignLockBox = async () => {
    if (lockBox)
      await bookLoanCar(moment(pickUpDate), props.estimateDetails, lockBox);
  };

  const checkIfDone = async () => {
    setShowLoader(true);
    try {
      const res = await ibsService.getTasksForUser(taskListId);
      const data: Appointment[] = (await res.json()).Data;

      const task = data.filter(
        (x) =>
          x.assignments.filter(
            (y) => y.assignedToId === props.estimateDetails.estimateId
          ).length > 0 && !x.isCompleted
      )[0];

      setIsDone(!!task);
      setPickTask(task);
    } catch (err) {
      throw new Error("Data could not be fetched!");
    } finally {
      setShowLoader(false);
    }
  };

  return (
    <div className="mt-1">
      <p className="heading">Job completed!</p>
      <br></br>
      {success && (
        <Message
          success
          header=""
          content="Your pick up details have been saved"
        />
      )}
      {isDone ? (
        <PickUpDisplay
          estimateDetails={props.estimateDetails}
          task={pickTask}
        ></PickUpDisplay>
      ) : (
        <>
          <p>Ready for pick up.</p>
          <PickUpStepper
            step={step}
            pickUpDate={pickUpDate}
            pickUpTime={pickUpTime}
          ></PickUpStepper>
          {step === 1 ? (
            <PickUpCalendar
              estimateDetails={props.estimateDetails}
              setDateSelected={setPickUpDate}
              preSelectedDate={pickUpDate}
            ></PickUpCalendar>
          ) : step === 2 ? (
            <PickUpTime
              preDateSelected={pickUpDate}
              setTimeSelected={setPickUpTime}
              preTimeSelected={pickUpTime}
              preLockBoxSelected={lockBox}
              setLockBox={setLockBox}
              setLockBoxAvailable={setIsLockBoxAvailable}
              setShowLoader={setShowLoader}
            ></PickUpTime>
          ) : step === 3 ? (
            <PickUpSummary
              estimateDetails={props.estimateDetails}
              pickUpDate={moment(pickUpDate)}
              pickUpTime={pickUpTime}
            ></PickUpSummary>
          ) : null}
          <div className="stepper-control">
            {step !== 1 && (
              <button onClick={() => setStep(step - 1)}>Back</button>
            )}
            {step !== 3 && (
              <button
                onClick={() => setStep(step + 1)}
                className={
                  (step === 1 && !pickUpDate) ||
                  (step === 2 && (!pickUpTime || !isLockBoxAvailable))
                    ? "disabled"
                    : ""
                }
              >
                Next
                {showLoader && (
                  <i
                    className="fa fa-refresh fa-spin"
                    style={{ marginLeft: "5px" }}
                  />
                )}
              </button>
            )}
            {step === 3 && (
              <button
                disabled={showLoader}
                onClick={(e) => {
                  onConfirm();
                }}
              >
                Confirm
                {showLoader && (
                  <i
                    className="fa fa-refresh fa-spin"
                    style={{ marginLeft: "5px" }}
                  />
                )}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default PickUp;
